import { useQuery } from "@tanstack/react-query";
import { withNamespaces } from "react-i18next";
import { toast } from "react-toastify";
import { Col, Row, Spinner } from "reactstrap";
import DataProtectionOnboardingService from "src/modules/data-protection/apis/DataProtectionOnboarding";
import BootstrapTable from "react-bootstrap-table-next";
import overlayFactory from 'react-bootstrap-table2-overlay';
import paginationFactory, { 
    PaginationProvider, 
    PaginationListStandalone,
    SizePerPageDropdownStandalone,
    PaginationTotalStandalone
} from 'react-bootstrap-table2-paginator';
import { EMPTY_LIST } from "src/common/constants";

const LogsTable = ({
    t
}) => {
    const service = DataProtectionOnboardingService.getInstance();

    const {
        data: processingLogs,
        isFetching: logsAreFetching,
        isFetched: logsFetched
    } = useQuery({
        queryKey: ["data-protection-other-assets-processing-records-logs"],
        queryFn: async () => {
            return await service.getProcessingErrorLogs();
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: (error) => {
            if (process.env.NODE_ENV === "development") console.error(error);
            toast(t("An error occurred while fetching processing status."), {
                type: "error"
            });
        }
    });

    const tableColumns = [
        {
            dataField: "data.type",
            text: t("Asset Type"),
            sort: true,
            key: 2
        },
        {
            dataField: "data.service",
            text: t("Service Name"),
            sort: true,
            key: 3
        },
        {
            dataField: "data.version",
            text: t("Version"),
            sort: true,
            key: 4
        },
        {
            dataField: "data.provider",
            text: t("Provider"),
            sort: true,
            key: 5
        },
        {
            dataField: "description",
            text: t("Error"),
            sort: false,
            key: 6,
            formatter: (cellContent) => {
                return (
                    <p className="text-danger">
                        {t(cellContent)}
                    </p>
                )
            }
        },
    ];

    const NoDataIndication = () => (
        (logsFetched && !processingLogs?.length) ? <div className="alert m-0" role="alert">
            <p style={{
                    textAlign: "center",
                    marginBottom: 0
                }}>
                {t(EMPTY_LIST)}
            </p>
        </div> : <></>
    )

    return (
        <div>
            <PaginationProvider
                pagination={
                    paginationFactory({
                        custom: true,
                        page: 1,
                        sizePerPage: 25,
                        totalSize: processingLogs?.length || 0,
                        withFirstAndLast: true,
                        alwaysShowAllBtns: true,
                        prePageText: <span><i className="ri-arrow-left-s-line"></i> {t('Back')}</span>,
                        nextPageText: <span>{t('Next')} <i className="ri-arrow-right-s-line"></i></span>,
                        prePageTitle: t('Pre page'),
                        firstPageTitle: t('First page'),
                        lastPageTitle: t('Last page'),
                        showTotal: false,
                        paginationTotalRenderer: (from, to, size) => {
                            return (
                                <span className="react-bootstrap-table-pagination-total">
                                    {`${t('Total Items')} ${size}`}
                                </span>
                            )
                        },
                        hideSizePerPage: false,
                        sizePerPageList: [
                            {
                                text: '25',
                                value: 25
                            },
                            {
                                text: '50',
                                value: 50
                            },
                            {
                                text: '100',
                                value: 100
                            }
                        ]
                    })
                }>
                {
                    ({
                        paginationProps,
                        paginationTableProps
                    }) => (
                        <>
                            <Row>
                                <Col sm="12">
                                    <h5 className="mt-4">{t('Import Log')} – {t('Failed Entries')}</h5>
                                    <p className="text-muted">
                                        {t('Only entries that could not be imported are shown below.')}
                                    </p>
                                    <BootstrapTable
                                        loading={logsAreFetching}
                                        overlay={overlayFactory({ spinner: <Spinner animation="border" variant="primary" size="md" />, text: `${t("Loading")}...` })}
                                        defaultSorted={[]}
                                        keyField={"id"}
                                        responsive
                                        bordered={false}
                                        data={processingLogs || []}
                                        striped={true}
                                        columns={tableColumns}
                                        wrapperClasses="table-responsive"
                                        classes={"table tpdd-table"}
                                        headerWrapperClasses={"thead-light"}
                                        noDataIndication={() => <NoDataIndication />}
                                        {...paginationTableProps}
                                    />
                                </Col>
                            </Row>

                            <Row>
                                <Col sm="12">
                                    <div className="tpdd-pagination-style-1">
                                        <PaginationTotalStandalone
                                            {...paginationProps}
                                        />

                                        <div>
                                            <PaginationListStandalone
                                                {...paginationProps}
                                            />

                                            <SizePerPageDropdownStandalone
                                                {...paginationProps}
                                            />
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </>
                    )
                }
            </PaginationProvider>
        </div>
    )
}

export default withNamespaces()(
    LogsTable
);